import defaultFetch from 'isomorphic-unfetch';
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer';
import type { IsAccommodationAvailableRawResponse } from '@/infrastructure/prices/availability/types';

interface AvailabilityContext {
  accoId: string;
  contextItemId: string;
  bookingGateId: string;
}

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string;
  };
  fetch: typeof defaultFetch;
  queryStringSerializer: QueryStringSerializer<AvailabilityContext>;
}

const fetchOptions: RequestInit = {
  referrerPolicy: 'no-referrer-when-downgrade',
  credentials: 'include',
  headers: {
    Accept: 'application/json',
  },
};

const FLIGHT_ONLY_DEFAULT_REQUEST_URL =
  'SharedFilters/GetFiltersApi?Participants[0][0]=1994-11-26&Participants[0][1]=1994-11-26&TransportType=Flight';

export const createAvailabilityApiClient = (dependencies: Partial<Dependencies> = {}) => {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
  } = dependencies as Dependencies;

  async function isAccommodationAvailable(criteria: AvailabilityContext) {
    const url = buildAPIUrl(criteria);

    const response = await fetch(url, fetchOptions);

    if (response.status !== 200)
      return {
        isAccommodationAvailable: false,
        isFlightOnly: false,
      };

    const { data } = (await response.json()) as IsAccommodationAvailableRawResponse;

    return {
      isAccommodationAvailable: data.isAccommodationAvailable,
      isFlightOnly: data.isFlightOnly,
    };
  }

  function buildAPIUrl({ accoId, contextItemId, bookingGateId }: AvailabilityContext) {
    const availabilityContext = {
      accoId,
      contextItemId,
      bookingGateId,
    };

    const queryString = queryStringSerializer.serialize({
      ...availabilityContext,
    });

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingGate/IsAccommodationAvailable?${queryString}`;
  }

  //Temporary workaround to determine if an acco is flight only
  function buildGetFiltersAPIUrl({ accoId, contextItemId, bookingGateId }: AvailabilityContext) {
    const availabilityContext = {
      accoId,
      contextItemId,
      bookingGateId,
    };

    const queryString = queryStringSerializer.serialize({
      ...availabilityContext,
    });

    return `${env.NEXT_PUBLIC_API_BASE_URL}/${FLIGHT_ONLY_DEFAULT_REQUEST_URL}&${queryString}`;
  }

  return { isAccommodationAvailable };
};
